import type { IconType } from 'react-icons';
import { 
  FiHome, FiUser, FiUsers, FiSettings, FiMenu, FiX, FiChevronDown, FiChevronRight,
  FiSearch, FiPlus, FiEdit, FiTrash2, FiSave, FiDownload, FiUpload,
  FiEye, FiEyeOff, FiLock, FiUnlock, FiMail, FiPhone, FiCalendar,
  FiClock, FiMapPin, FiHeart, FiStar, FiBookmark, FiShare2,
  FiExternalLink, FiCopy, FiCheck, FiAlertCircle, FiInfo, FiHelpCircle,
  FiArrowLeft, FiArrowRight, FiArrowUp, FiArrowDown, FiRefreshCw,
  FiFilter, FiGrid, FiList, FiMoreHorizontal,
  FiMoreVertical, FiMinus, FiMaximize2, FiMinimize2, FiZoomIn, FiZoomOut,
  FiFolder, FiFileText, FiClipboard, FiDatabase, FiTarget, FiShield,
  FiMessageSquare, FiActivity, FiBriefcase, FiCompass
} from 'react-icons/fi';
import { 
  GiDna1, GiDna2, GiAtom, GiMicroscope, GiTestTubes,
  GiPill, GiBrain, GiMuscleUp, GiWeight
} from 'react-icons/gi';
import { 
  RiFlaskFill, RiTestTubeFill, RiMicroscopeFill, RiDnaFill,
  RiHealthBookFill, RiMedicineBottleFill, RiStethoscopeFill, RiSparklingFill
} from 'react-icons/ri';

// Icon size variants
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

// Icon categories for better organization
export type IconCategory = 'navigation' | 'action' | 'status' | 'genetics' | 'health' | 'ui';

// Icon mapping interface
export interface IconMapping {
  [key: string]: IconType;
}

// Size mapping for consistent sizing
export const ICON_SIZES: Record<IconSize, number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32
};

// Primary icon library (Feather Icons) - 80% of usage
export const PRIMARY_ICONS: IconMapping = {
  // Navigation
  home: FiHome,
  user: FiUser,
  users: FiUsers,
  settings: FiSettings,
  menu: FiMenu,
  close: FiX,
  'chevron-down': FiChevronDown,
  'chevron-right': FiChevronRight,
  'arrow-left': FiArrowLeft,
  'arrow-right': FiArrowRight,
  'arrow-up': FiArrowUp,
  'arrow-down': FiArrowDown,
  
  // Actions
  search: FiSearch,
  plus: FiPlus,
  edit: FiEdit,
  delete: FiTrash2,
  save: FiSave,
  download: FiDownload,
  upload: FiUpload,
  copy: FiCopy,
  check: FiCheck,
  refresh: FiRefreshCw,
  filter: FiFilter,
  
  // Status
  eye: FiEye,
  'eye-off': FiEyeOff,
  lock: FiLock,
  unlock: FiUnlock,
  'alert-circle': FiAlertCircle,
  info: FiInfo,
  'help-circle': FiHelpCircle,
  
  // Communication
  mail: FiMail,
  phone: FiPhone,
  share: FiShare2,
  'external-link': FiExternalLink,
  chat: FiMessageSquare,
  
  // Time & Location
  calendar: FiCalendar,
  clock: FiClock,
  'map-pin': FiMapPin,
  
  // UI Elements
  heart: FiHeart,
  star: FiStar,
  bookmark: FiBookmark,
  grid: FiGrid,
  list: FiList,
  'more-horizontal': FiMoreHorizontal,
  'more-vertical': FiMoreVertical,
  minus: FiMinus,
  maximize: FiMaximize2,
  minimize: FiMinimize2,
  'zoom-in': FiZoomIn,
  'zoom-out': FiZoomOut,
  folder: FiFolder,
  'file-text': FiFileText,
  clipboard: FiClipboard,
  database: FiDatabase,
  target: FiTarget,
  shield: FiShield,
  activity: FiActivity,
  briefcase: FiBriefcase,
  compass: FiCompass,
};

// Genetics-specific icons (Game Icons)
export const GENETICS_ICONS: IconMapping = {
  dna: GiDna1,
  'dna-double': GiDna2,
  atom: GiAtom,
  microscope: GiMicroscope,
  'test-tubes': GiTestTubes
};

// Health-specific icons (Game Icons)
export const HEALTH_ICONS: IconMapping = {
  pill: GiPill,
  brain: GiBrain,
  'muscle-up': GiMuscleUp,
  weight: GiWeight
};

// Special effects icons (Remix Icons)
export const SPECIAL_ICONS: IconMapping = {
  'flask-fill': RiFlaskFill,
  'test-tube-fill': RiTestTubeFill,
  'microscope-fill': RiMicroscopeFill,
  'dna-fill': RiDnaFill,
  'health-book-fill': RiHealthBookFill,
  'medicine-bottle-fill': RiMedicineBottleFill,
  'stethoscope-fill': RiStethoscopeFill,
  'sparkling-fill': RiSparklingFill
};

// Combined icon registry
export const ICON_REGISTRY: IconMapping = {
  ...PRIMARY_ICONS,
  ...GENETICS_ICONS,
  ...HEALTH_ICONS,
  ...SPECIAL_ICONS
};

// Icon service class
export class IconService {
  private static instance: IconService;
  private iconCache: Map<string, IconType> = new Map();

  private constructor() {
    // Pre-populate cache with all registered icons
    Object.entries(ICON_REGISTRY).forEach(([name, icon]) => {
      this.iconCache.set(name, icon);
    });
  }

  public static getInstance(): IconService {
    if (!IconService.instance) {
      IconService.instance = new IconService();
    }
    return IconService.instance;
  }

  /**
   * Get an icon by name
   */
  public getIcon(name: string): IconType | null {
    return this.iconCache.get(name) || null;
  }

  /**
   * Check if an icon exists
   */
  public hasIcon(name: string): boolean {
    return this.iconCache.has(name);
  }

  /**
   * Get all available icon names
   */
  public getAvailableIcons(): string[] {
    return Array.from(this.iconCache.keys()).sort();
  }

  /**
   * Get icons by category
   */
  public getIconsByCategory(category: IconCategory): string[] {
    const categoryMap: Record<IconCategory, string[]> = {
      navigation: ['home', 'user', 'settings', 'menu', 'close', 'chevron-down', 'chevron-right', 'arrow-left', 'arrow-right', 'arrow-up', 'arrow-down', 'shield'],
      action: ['search', 'plus', 'edit', 'delete', 'save', 'download', 'upload', 'copy', 'check', 'refresh', 'filter', 'target'],
      status: ['eye', 'eye-off', 'lock', 'unlock', 'alert-circle', 'info', 'help-circle'],
      genetics: ['dna', 'dna-double', 'atom', 'microscope', 'test-tubes', 'flask-fill', 'test-tube-fill', 'microscope-fill', 'dna-fill'],
      health: ['pill', 'brain', 'muscle-up', 'weight', 'health-book-fill', 'medicine-bottle-fill', 'stethoscope-fill'],
      ui: ['heart', 'star', 'bookmark', 'grid', 'list', 'more-horizontal', 'more-vertical', 'minus', 'maximize', 'minimize', 'zoom-in', 'zoom-out', 'folder', 'file-text', 'clipboard', 'database', 'chat']
    };
    
    return categoryMap[category] || [];
  }

  /**
   * Get size in pixels for a given size variant
   */
  public getSize(size: IconSize): number {
    return ICON_SIZES[size];
  }

  /**
   * Validate icon name
   */
  public validateIconName(name: string): boolean {
    return this.hasIcon(name);
  }
}

// Export singleton instance
export const iconService = IconService.getInstance();
